<script setup lang="ts">
/**
 * The landing page and the paywall are one page (§3.1, mockup 1): signed out it
 * invites you to sign in; signed in without a membership it shows the 9 € card;
 * with one, it sends you on to the projects.
 *
 * Playful here, where nobody is working (§11a), but composed rather than
 * assembled: one centred column, big display type, one action. The earlier
 * version was glow blobs, a pill badge, dotted chips and a glowing card —
 * stock parts that read as generated. The footer's Puida separator grows out of this page's black (the
 * route's `footerFrom`), so the page itself draws no second one.
 */
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { isSignedIn, signIn } from '@/lib/session';
import { useMe } from '@/lib/me';
import { api } from '@/lib/api';

const route = useRoute();
const router = useRouter();
const me = useMe();

/** Where to go after signing in or paying: the page they asked for, if any. */
const next = computed(() => {
  const n = typeof route.query.next === 'string' ? route.query.next : '';
  return n.startsWith('/') && !n.startsWith('//') ? n : null;
});

watch(
  me.entitled,
  (entitled) => {
    if (entitled) void router.replace(next.value ?? '/projects');
  },
  { immediate: true },
);

// An invitation link must be reachable before paying (§3.1): it goes on
// regardless of entitlement.
watch(
  () => [isSignedIn.value, next.value] as const,
  ([signedIn, n]) => {
    if (signedIn && n?.startsWith('/invites/')) void router.replace(n);
  },
  { immediate: true },
);

const billing = useQuery({ queryKey: ['billing'], queryFn: api.billing, enabled: isSignedIn });
const invites = useQuery({ queryKey: ['my-invites'], queryFn: api.myInvites, enabled: isSignedIn });

const checkout = useMutation({
  mutationFn: () => api.checkout(next.value ?? undefined),
  // Stripe's page, then back to /billing/return.
  onSuccess: ({ url }) => location.assign(url),
});

// The public products, each linking to its landing page. Members-only ones
// (Lasti, Torjua, Tiuha, Tilus) would answer "not found" to a stranger.
const products = [
  { slug: 'hinguere', name: 'Hinguere' },
  { slug: 'sulle', name: 'Sulle' },
  { slug: 'protocache', name: 'Protocache' },
  { slug: 'tapelang', name: 'Tapelang' },
  { slug: 'leivos', name: 'Leivos' },
  { slug: 'bodaus', name: 'Bodaus' },
  { slug: 'tools', name: 'Puida Tools' },
];
</script>

<template>
  <div class="flex min-h-[calc(100svh-12rem)] items-center bg-[#0a0a0a] text-slate-200">
    <section class="mx-auto w-full max-w-4xl px-4 py-20 text-center sm:px-8">
      <p class="text-sm font-medium text-slate-400">
        <span class="text-lime-400">Puida Oy</span> · {{ $t('landing.badge') }}
      </p>
      <h1 class="mt-5 text-5xl leading-[1.02] font-bold tracking-tight text-white sm:text-8xl">
        {{ $t('landing.titleLine1') }}<br />
        <span class="text-lime-400">{{ $t('landing.titleLine2') }}</span>
      </h1>
      <p class="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-400 sm:text-xl">
        {{ $t('landing.lede') }}
      </p>

      <div class="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
        <button
          v-if="!isSignedIn"
          type="button"
          class="min-h-13 rounded-full bg-sulje px-8 text-base font-bold text-sulje-ink transition-colors hover:bg-lime-300"
          @click="signIn(next ?? undefined)"
        >
          {{ $t('landing.signIn') }}
        </button>
        <button
          v-else-if="billing.data.value?.available"
          type="button"
          class="min-h-13 rounded-full bg-sulje px-8 text-base font-bold text-sulje-ink transition-colors hover:bg-lime-300 disabled:opacity-60"
          :disabled="checkout.isPending.value"
          @click="checkout.mutate()"
        >
          {{ $t('paywall.join') }}
        </button>
        <p v-if="!isSignedIn || billing.data.value?.available" class="text-slate-300">
          <span class="font-display text-2xl font-bold text-white">9 €</span>
          {{ $t('paywall.perMonth') }}
        </p>
      </div>

      <p v-if="checkout.isError.value" role="alert" class="mt-4 text-sm text-rose-300">
        {{ $t('errors.generic') }}
      </p>
      <p
        v-if="isSignedIn && billing.isSuccess.value && !billing.data.value?.available"
        class="mx-auto mt-2 max-w-md text-sm text-slate-300"
      >
        <span v-if="me.data.value" class="mb-1 block text-slate-400">
          {{ $t('paywall.signedInAs', { who: me.data.value.email ?? me.data.value.displayName }) }}
        </span>
        {{ $t('paywall.checkoutSoon') }}
      </p>

      <ul
        class="mx-auto mt-6 flex max-w-2xl list-none flex-wrap justify-center gap-x-5 gap-y-1 p-0 text-sm text-slate-400"
      >
        <li v-for="n in 3" :key="n" class="whitespace-nowrap">
          <span class="text-lime-400" aria-hidden="true">— </span>{{ $t(`paywall.point${n}`) }}
        </li>
      </ul>
      <p class="mx-auto mt-2 max-w-xl text-xs text-slate-500">{{ $t('paywall.noTrial') }}</p>

      <div
        v-if="invites.data.value?.items.length"
        class="mx-auto mt-8 flex max-w-md flex-col gap-3"
      >
        <RouterLink
          v-for="inv in invites.data.value.items"
          :key="inv.id"
          :to="`/invites/${inv.id}`"
          class="rounded-2xl border border-amber-400/30 px-5 py-4 text-left text-sm text-slate-300 no-underline hover:border-amber-400"
        >
          <strong class="text-white">
            {{ $t('invite.title', { target: inv.project?.name ?? inv.org.name }) }}
          </strong>
          <span class="mt-1 block font-semibold text-amber-300">{{ $t('invite.open') }} →</span>
        </RouterLink>
      </div>

      <nav :aria-label="$t('landing.products')" class="mt-16 border-t border-white/10 pt-8">
        <ul class="flex list-none flex-wrap justify-center gap-x-8 gap-y-3 p-0">
          <li v-for="p in products" :key="p.slug">
            <RouterLink
              :to="`/puida/${p.slug}`"
              class="font-display text-lg font-semibold text-slate-500 no-underline transition-colors hover:text-white"
            >
              {{ p.name }}
            </RouterLink>
          </li>
        </ul>
      </nav>
    </section>
  </div>
</template>
